import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faQrcode, faTimes, faSave, faSync, faCheckCircle, faExclamationTriangle,
  faCopy, faEye, faEyeSlash, faQuestionCircle, faClock
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import axios from '../../config/axiosConfig';

const TOTPConfiguration = ({ profile, isDarkMode, onClose, onSave }) => {
  const [setupMethod, setSetupMethod] = useState('qr'); // 'qr' or 'manual'
  const [qrData, setQrData] = useState(null);
  const [manualSecret, setManualSecret] = useState('');
  const [algorithm, setAlgorithm] = useState('SHA1');
  const [digits, setDigits] = useState(6);
  const [period, setPeriod] = useState(30);
  const [verificationCode, setVerificationCode] = useState('');
  const [currentCode, setCurrentCode] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    // If profile already has TOTP config, load it
    if (profile?.mfaConfig?.totp?.secret) {
      setSetupMethod('manual');
      setManualSecret(profile.mfaConfig.totp.secret);
      setAlgorithm(profile.mfaConfig.totp.algorithm || 'SHA1');
      setDigits(profile.mfaConfig.totp.digits || 6);
      setPeriod(profile.mfaConfig.totp.period || 30);
      loadCurrentCode(profile.mfaConfig.totp.secret);
    } else {
      // Generate new QR code
      generateQRCode();
    }
  }, [profile]);

  const generateQRCode = async () => {
    try {
      setGenerating(true);
      const accountName = profile?.automatedLogin?.username || profile?.name || 'User';
      const response = await axios.post('/totp/generate-qr', {
        accountName,
        issuer: 'PurpleHQ',
        algorithm,
        digits,
        period
      });

      setQrData(response.data);
      setManualSecret(response.data.secret);
      loadCurrentCode(response.data.secret);
    } catch (error) {
      console.error('Failed to generate QR:', error);
      toast.error('Failed to generate QR code');
    } finally {
      setGenerating(false);
    }
  };

  const loadCurrentCode = async (secret) => {
    if (!secret) return;

    try {
      const response = await axios.post('/totp/current-code', {
        secret,
        algorithm,
        digits,
        period
      });
      setCurrentCode(response.data);

      // Refresh code before it expires
      const refreshIn = (response.data.timeRemaining - 1) * 1000;
      if (refreshIn > 0) {
        setTimeout(() => loadCurrentCode(secret), refreshIn);
      }
    } catch (error) {
      console.error('Failed to load current code:', error);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== digits) {
      toast.error(`Code must be ${digits} digits`);
      return;
    }

    try {
      setVerifying(true);
      const response = await axios.post('/totp/verify', {
        secret: manualSecret,
        code: verificationCode,
        algorithm,
        digits,
        period,
        window: 1 // Allow ±1 time window
      });

      if (response.data.valid) {
        toast.success('✓ Code verified successfully!');
        // Save configuration
        await handleSave();
      } else {
        toast.error('✗ Invalid code. Please try again.');
      }
    } catch (error) {
      console.error('Verification failed:', error);
      toast.error('Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleSave = async () => {
    const totpConfig = {
      enabled: true,
      type: 'totp',
      totp: {
        secret: manualSecret,
        algorithm,
        digits,
        period,
        issuer: 'PurpleHQ',
        accountName: profile?.automatedLogin?.username || profile?.name || 'User'
      }
    };

    try {
      const updatedProfile = {
        ...profile,
        mfaConfig: totpConfig
      };

      await axios.put(`/auth-profiles/${profile._id}`, updatedProfile);

      toast.success('✓ TOTP configured successfully!');
      if (onSave) {
        onSave(totpConfig);
      }
      onClose();
    } catch (error) {
      console.error('Failed to save TOTP config:', error);
      toast.error('Failed to save configuration');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const containerClass = isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900';
  const borderClass = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputClass = isDarkMode
    ? 'bg-gray-700 border-gray-600 text-gray-100'
    : 'bg-white border-gray-300 text-gray-900';

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${isDarkMode ? 'bg-black bg-opacity-75' : 'bg-gray-900 bg-opacity-50'}`}>
      <div className={`${containerClass} rounded-lg shadow-xl w-full max-w-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderClass}`}>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faQrcode} className="text-blue-500" />
            <div>
              <h2 className="text-xl font-semibold">TOTP Configuration</h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Time-based One-Time Password for MFA
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowHelp(!showHelp)}
              className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="Help"
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="text-blue-500" />
            </button>
            <button
              onClick={onClose}
              className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              title="Close"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Help Section */}
        {showHelp && (
          <div className={`m-4 p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 bg-opacity-20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
            <h3 className="font-semibold mb-2">How TOTP Works</h3>
            <ul className="text-sm space-y-1 list-disc list-inside">
              <li>TOTP generates a new 6-digit code every 30 seconds</li>
              <li>Compatible with Google Authenticator, Authy, Microsoft Authenticator, etc.</li>
              <li>Codes are generated using a shared secret key</li>
              <li>After setup, codes will be automatically generated during automated logins</li>
              <li>Scan the QR code with your authenticator app or enter the secret manually</li>
            </ul>
          </div>
        )}

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Setup Method */}
          <div>
            <label className="block text-sm font-semibold mb-2">Setup Method:</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="setupMethod"
                  value="qr"
                  checked={setupMethod === 'qr'}
                  onChange={(e) => setSetupMethod(e.target.value)}
                  className="w-4 h-4"
                />
                <span>Scan QR Code</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="setupMethod"
                  value="manual"
                  checked={setupMethod === 'manual'}
                  onChange={(e) => setSetupMethod(e.target.value)}
                  className="w-4 h-4"
                />
                <span>Enter Manually</span>
              </label>
            </div>
          </div>

          {/* QR Code Setup */}
          {setupMethod === 'qr' && (
            <div className={`p-4 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
              <div className="flex flex-col md:flex-row gap-4">
                {/* QR Code */}
                <div className="flex-shrink-0">
                  {generating ? (
                    <div className="w-48 h-48 flex items-center justify-center">
                      <FontAwesomeIcon icon={faSync} spin size="2x" />
                    </div>
                  ) : qrData?.qrCodeDataUrl ? (
                    <img
                      src={qrData.qrCodeDataUrl}
                      alt="TOTP QR Code"
                      className="w-48 h-48 border-4 border-white rounded"
                    />
                  ) : (
                    <div className="w-48 h-48 flex items-center justify-center border-2 border-dashed border-gray-400 rounded">
                      <span className="text-gray-500">No QR code</span>
                    </div>
                  )}
                </div>

                {/* Instructions */}
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">Setup Instructions:</h3>
                  <ol className="text-sm space-y-2 list-decimal list-inside">
                    <li>Open your authenticator app (Google Authenticator, Authy, etc.)</li>
                    <li>Tap "+" or "Add account"</li>
                    <li>Scan this QR code with your device camera</li>
                    <li>Enter the 6-digit code below to verify</li>
                  </ol>

                  {/* Secret (for backup) */}
                  {qrData?.secret && (
                    <div className="mt-4">
                      <label className="text-xs text-gray-500">Secret (for manual entry):</label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className={`flex-1 px-2 py-1 rounded text-xs ${inputClass}`}>
                          {showSecret ? qrData.secret : '••••••••••••••••••••••••••'}
                        </code>
                        <button
                          onClick={() => setShowSecret(!showSecret)}
                          className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                          title={showSecret ? 'Hide' : 'Show'}
                        >
                          <FontAwesomeIcon icon={showSecret ? faEyeSlash : faEye} className="text-xs" />
                        </button>
                        <button
                          onClick={() => copyToClipboard(qrData.secret)}
                          className={`p-1 rounded ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'}`}
                          title="Copy"
                        >
                          <FontAwesomeIcon icon={faCopy} className="text-xs" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Manual Setup */}
          {setupMethod === 'manual' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-semibold mb-1">Secret Key:</label>
                <div className="flex items-center gap-2">
                  <input
                    type={showSecret ? 'text' : 'password'}
                    value={manualSecret}
                    onChange={(e) => {
                      setManualSecret(e.target.value);
                      if (e.target.value) loadCurrentCode(e.target.value);
                    }}
                    className={`flex-1 px-3 py-2 rounded border ${inputClass}`}
                    placeholder="Enter TOTP secret key"
                  />
                  <button
                    onClick={() => setShowSecret(!showSecret)}
                    className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    title={showSecret ? 'Hide' : 'Show'}
                  >
                    <FontAwesomeIcon icon={showSecret ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              {/* Advanced Settings */}
              <div className={`p-3 rounded-lg border ${borderClass} ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                <h3 className="text-sm font-semibold mb-2">Advanced Settings:</h3>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs mb-1">Algorithm:</label>
                    <select
                      value={algorithm}
                      onChange={(e) => setAlgorithm(e.target.value)}
                      className={`w-full px-2 py-1 rounded border text-sm ${inputClass}`}
                    >
                      <option value="SHA1">SHA1</option>
                      <option value="SHA256">SHA256</option>
                      <option value="SHA512">SHA512</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Digits:</label>
                    <select
                      value={digits}
                      onChange={(e) => setDigits(parseInt(e.target.value))}
                      className={`w-full px-2 py-1 rounded border text-sm ${inputClass}`}
                    >
                      <option value={6}>6</option>
                      <option value={7}>7</option>
                      <option value={8}>8</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1">Period (s):</label>
                    <input
                      type="number"
                      value={period}
                      onChange={(e) => setPeriod(parseInt(e.target.value))}
                      className={`w-full px-2 py-1 rounded border text-sm ${inputClass}`}
                      min={10}
                      max={120}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Current Code Display */}
          {currentCode && (
            <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-purple-900 bg-opacity-20 border border-purple-800' : 'bg-purple-50 border border-purple-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-semibold block mb-1">Current Code:</label>
                  <div className="flex items-center gap-3">
                    <div className="text-3xl font-mono font-bold tracking-widest">
                      {currentCode.currentCode.match(/.{1,3}/g).join(' ')}
                    </div>
                    <button
                      onClick={() => copyToClipboard(currentCode.currentCode)}
                      className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                      title="Copy code"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-xs text-gray-500 mb-1">Refreshes in:</div>
                  <div className="text-2xl font-bold flex items-center gap-1">
                    <FontAwesomeIcon icon={faClock} className="text-sm" />
                    {currentCode.timeRemaining}s
                  </div>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                Next code: {currentCode.nextCode}
              </div>
            </div>
          )}

          {/* Verification */}
          <div>
            <label className="block text-sm font-semibold mb-1">Verification Code:</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, digits))}
                className={`flex-1 px-3 py-2 rounded border ${inputClass} text-center text-lg font-mono tracking-widest`}
                placeholder={`Enter ${digits}-digit code`}
                maxLength={digits}
              />
              <button
                onClick={handleVerify}
                disabled={verificationCode.length !== digits || verifying}
                className={`px-4 py-2 rounded ${
                  verificationCode.length === digits && !verifying
                    ? isDarkMode
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-green-500 hover:bg-green-600'
                    : 'bg-gray-400 cursor-not-allowed'
                } text-white`}
              >
                {verifying ? (
                  <FontAwesomeIcon icon={faSync} spin />
                ) : (
                  <FontAwesomeIcon icon={faCheckCircle} />
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Enter the code from your authenticator app to verify and save
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between p-4 border-t ${borderClass}`}>
          <div className="text-xs text-gray-500">
            TOTP will be used during automated logins for this profile
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-600 hover:bg-gray-700' : 'bg-gray-500 hover:bg-gray-600'} text-white`}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TOTPConfiguration;
